import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Container,
  useMediaQuery,
  useTheme as useMuiTheme,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
} from '@mui/material';
import {
  Menu,
  Close,
} from '@mui/icons-material';
import { useTheme } from '../context/ThemeContext';

const Header: React.FC = () => {
  const { theme: customTheme } = useTheme();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMobileOpen(false); // Close mobile drawer after navigation
    }
  };

  const navItems = [
    { label: 'Home', id: 'home' },
    { label: 'Services', id: 'services' },
    { label: 'Gallery', id: 'gallery' },
    { label: 'Contact', id: 'contact' },
  ];

  // Track scroll position to highlight active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100; // Offset for header height

      // Check each section to see which one is in view
      for (let i = navItems.length - 1; i >= 0; i--) {
        const section = document.getElementById(navItems[i].id);
        if (section) {
          const sectionTop = section.offsetTop;
          if (scrollPosition >= sectionTop) {
            setActiveSection(navItems[i].id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Call once on mount to set initial active section

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const drawer = (
    <Box sx={{ width: 280, pt: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" px={2} mb={2}>
        <Box display="flex" alignItems="center">
          <Box
            component="img"
            src="/logo-transparent.png"
            alt="Ishaan Construction Works"
            sx={{
              width: 70,
              height: 70,
              mr: 1.5,
              objectFit: 'contain',
            }}
          />
          <Typography variant="h6" fontWeight="bold" sx={{ color: 'text.primary' }}>
            MENU
          </Typography>
        </Box>
        <IconButton onClick={handleDrawerToggle}>
          <Close />
        </IconButton>
      </Box>
      <Divider />
      <List>
        {navItems.map((item) => {
          const isActive = activeSection === item.id;
          return (
            <ListItem 
              key={item.id}
              component="div" 
              onClick={() => scrollToSection(item.id)} 
              sx={{ 
                cursor: 'pointer', 
                '&:hover': { backgroundColor: 'action.hover' },
                py: 2,
                backgroundColor: isActive ? `${customTheme.primaryColor}15` : 'transparent',
                borderLeft: isActive ? `4px solid ${customTheme.primaryColor}` : '4px solid transparent',
              }}
            >
              <ListItemText 
                primary={item.label}
                primaryTypographyProps={{ 
                  fontWeight: isActive ? 700 : 600, 
                  color: isActive ? customTheme.primaryColor : 'text.primary',
                  fontSize: '1.1rem',
                }}
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{
          background: `linear-gradient(180deg, ${customTheme.primaryColor}05 0%, transparent 100%)`,
          backgroundColor: 'background.paper',
          borderBottom: '2px solid',
          borderImage: `linear-gradient(90deg, ${customTheme.primaryColor} 0%, ${customTheme.accentColor} 50%, ${customTheme.primaryColor} 100%) 1`,
          backdropFilter: 'blur(10px)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar 
            disableGutters
            sx={{ 
              py: { xs: 1.5, sm: 2 },
              minHeight: { xs: 70, sm: 80 },
            }}
          >
            {/* Logo Section */}
            <Box 
              display="flex" 
              alignItems="center" 
              flexGrow={1}
              sx={{ minWidth: 0 }} // Prevents overflow
            >
              <Box
                component="img"
                src="/logo-transparent.png"
                alt="Ishaan Construction Works"
                sx={{
                  width: { xs: 80, sm: 95, md: 110 },
                  height: { xs: 80, sm: 95, md: 110 },
                  mr: { xs: 1.5, sm: 2.5 },
                  flexShrink: 0,
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 2px 10px rgba(0,0,0,0.12))',
                }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography 
                  variant="h6" 
                  component="div" 
                  fontWeight="bold"
                  sx={{
                    fontSize: { xs: '0.95rem', sm: '1.15rem', md: '1.3rem' },
                    lineHeight: 1.2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: { xs: 'nowrap', sm: 'normal' },
                    background: `linear-gradient(135deg, ${customTheme.primaryColor} 0%, ${customTheme.accentColor} 100%)`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  ISHAAN CONSTRUCTION WORKS
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                    display: { xs: 'none', sm: 'block' },
                    color: 'text.secondary',
                    fontWeight: 500,
                  }}
                >
                  Building Dreams, Creating Excellence
                </Typography>
              </Box>
            </Box>

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box display="flex" alignItems="center" gap={0.5}>
                {navItems.map((item) => {
                  const isActive = activeSection === item.id;
                  return (
                    <Button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      sx={{
                        color: isActive ? 'white' : 'text.primary',
                        background: isActive 
                          ? `linear-gradient(135deg, ${customTheme.primaryColor} 0%, ${customTheme.accentColor} 100%)`
                          : 'transparent',
                        textTransform: 'none',
                        fontSize: '1rem',
                        fontWeight: isActive ? 700 : 500,
                        px: 2.5,
                        py: 1,
                        borderRadius: 2,
                        position: 'relative',
                        boxShadow: isActive ? `0 4px 12px ${customTheme.primaryColor}30` : 'none',
                        '&:hover': {
                          backgroundColor: isActive 
                            ? `linear-gradient(135deg, ${customTheme.primaryColor}dd 0%, ${customTheme.accentColor}dd 100%)`
                            : customTheme.primaryColor + '10',
                          transform: 'translateY(-1px)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {item.label}
                    </Button>
                  );
                })}
              </Box>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                aria-label="open drawer"
                edge="end"
                onClick={handleDrawerToggle}
                sx={{
                  background: `linear-gradient(135deg, ${customTheme.primaryColor} 0%, ${customTheme.accentColor} 100%)`,
                  color: 'white',
                  borderRadius: 2,
                  width: 44,
                  height: 44,
                  boxShadow: `0 4px 12px ${customTheme.primaryColor}30`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${customTheme.primaryColor}dd 0%, ${customTheme.accentColor}dd 100%)`,
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <Menu />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Header;

